/**
 * Manual Structure Host Tool
 *
 * Click-to-place polygon drawing for STRUCTURE annotations — the manual
 * counterpart to the LineTool-hosted Magic Beam / Magic Column tools. Draws via
 * the `MeasureSubTool` (MeasureEngine) and commits through the same
 * `StructureKindAdapter` / `create_structure` path.
 *
 * Since structures were split into fixed BEAM and COLUMN kinds, the manual tool
 * defaults to COLUMN and exposes a column/beam toggle in its settings — flipping
 * it just re-points the adapter's kind, applied on the next commit.
 */

import { StructureKind } from 'shared-types';
import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { SubToolHosting } from '../../SubToolHosting';
import { MeasureSubTool } from './MeasureSubTool';
import { StructureKindAdapter } from './line-tool/StructureKindAdapter';
import { fullFloorSlabIsolationStrategy, setStructureSphereScale } from './StructureHostTool';
import { IsolationSpec, boundsFromPlanes } from '../../../utils/FloorIsolation';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { t } from '../../../translation/markers';

/** Settings key for the column/beam toggle. */
const KIND_SETTING = 'structureKind';

/** Toolbar ICON_MAP key per structure kind — the tool button reflects the toggle. */
const ICON_BY_KIND: Record<StructureKind, string> = {
  [StructureKind.COLUMN]: 'Column',
  [StructureKind.BEAM]: 'Beam',
};

export class ManualStructureHostTool extends SubToolHosting(FloorplanToolBase) {
  readonly name = 'manual_structure_draw';

  /** Typed reference so the settings toggle can re-point the committed kind. */
  private readonly structureAdapter: StructureKindAdapter;

  /** Current toolbar icon key — swapped by the column/beam toggle. */
  private iconKey: string = ICON_BY_KIND[StructureKind.COLUMN];

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.structureAdapter = new StructureKindAdapter(StructureKind.COLUMN);
    const subtool = new MeasureSubTool(viewer, persona, this.structureAdapter);
    const ctx = this.structureAdapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(subtool, this.structureAdapter, ctx);
  }

  /** Dynamic toolbar icon — the Toolbar prefers this over the static config icon. */
  getToolbarIcon(): string {
    return this.iconKey;
  }

  // Vertex spheres full size while a structure tool is active, half size outside.

  protected onEnable(): void {
    super.onEnable();
    setStructureSphereScale(this.viewer, 1);
  }

  protected onDisable(): void {
    setStructureSphereScale(this.viewer, 0.5);
    super.onDisable();
  }

  protected onFloorChanged(): void {
    this.pushBasePlane();
  }

  /** Full-floor slab (base → next floor, slab included) — same as the magic tools. */
  protected getIsolationSpec(): IsolationSpec {
    const floorGroupId = this.currentFloorGroupIdentifier;
    if (!floorGroupId || !this.currentFloorFloorPlane) {
      return super.getIsolationSpec();
    }
    const bounds = this.viewer.floorIsolationManager.resolveLevelBounds(floorGroupId) ?? boundsFromPlanes(floorGroupId, this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
    return {
      base: fullFloorSlabIsolationStrategy({
        floorGroupId,
        floor: this.currentFloorFloorPlane,
        ceiling: this.currentFloorCeilingPlane,
        bounds,
      }),
    };
  }

  /** Column/beam toggle — the manual tool has no auto-classification. */
  getSettingsSchema(): ToolSettingsSchema {
    return {
      [KIND_SETTING]: {
        type: 'select',
        label: t('Structure type'),
        description: 'Whether the drawn shape becomes a column or a beam',
        defaultValue: StructureKind.COLUMN,
        options: [
          { label: t('Column'), value: StructureKind.COLUMN },
          { label: t('Beam'), value: StructureKind.BEAM },
        ],
      },
    };
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    if (event.key === KIND_SETTING) {
      const kind = event.value as StructureKind;
      this.structureAdapter.setKind(kind);
      this.iconKey = ICON_BY_KIND[kind];
      // Nudge the React Toolbar to re-read getToolbarIcon() for this tool.
      this.viewer.scene.dispatchEvent({ type: 'tool_icon_changed', tool: this });
    }
  }
}
